import { FC, Fragment, useCallback, useState } from "react";
import { CustomCellRendererProps } from "ag-grid-react";
import Stack from "@mui/material/Stack";
import { Typography } from "@mui/material";
import { GridApi } from "ag-grid-community";
import { grey } from "@mui/material/colors";
import { useWillUnmount } from "rooks";
import { ResourcesAgGrid } from "./ResourcesAgGrid";
import { AGGridColumnsToolPanel } from "../../../ag-grid/components/AGGridColumnsToolPanel";
import {
  getResourceGridSize,
  resourceDetailGridPadding,
} from "../../utils/constants/sizes";
import type { RowGroupOpenedEvent } from "ag-grid-community/dist/types/src/events";
import { AllTypeResourcesData } from "@/store/resources-grid/utils/types/resources";
import { useAppDispatch } from "@/store/hooks";
import { setGridDetailRowsHeights } from "@/store/resources-grid/resourcesGridSlice";
import { ResourceType } from "@/services/cloudchipr.api";
import { LiveResourcesGridFilterContent } from "@/components/pages/live-usage-mgmt/components/resources-area/toolbar/LiveResourcesGridFilterContent";
import { LiveUsageResourceGridCsvExport } from "@/components/pages/live-usage-mgmt/components/resources-area/toolbar/LiveUsageResourceGridCsvExport";

interface ResourcesDetailGridProps
  extends CustomCellRendererProps<AllTypeResourcesData> {
  masterGridId: string;
  masterResourceType: ResourceType;
}

export const ResourcesDetailGrid: FC<ResourcesDetailGridProps> = ({
  node: parentRow,
  masterGridId,
  api,
  masterResourceType,
}) => {
  const dispatch = useAppDispatch();

  const [detailGridApi, setDetailGridApi] = useState<GridApi | null>(null);

  const data =
    parentRow.data && "child_resource" in parentRow.data
      ? parentRow.data?.child_resource
      : [];

  const resourceType = data?.resources?.at(0)?.resource_type;

  const parentId = parentRow.id ?? "";

  const onChildRowGroupOpened = useCallback(
    (params: RowGroupOpenedEvent) => {
      if (!parentId) {
        return;
      }

      const expanded = params.expanded;
      const childrenCount = params.data?.child_resource?.resources?.length;
      const height = expanded ? getResourceGridSize(childrenCount) : 0;

      dispatch(
        setGridDetailRowsHeights({
          height,
          gridId: masterGridId,
          rowId: parentId,
        }),
      );
    },
    [dispatch, masterGridId, parentId],
  );

  useWillUnmount(() => {
    dispatch(
      setGridDetailRowsHeights({
        height: 0,
        gridId: masterGridId,
        rowId: parentId,
      }),
    );
  });

  if (!data) {
    return null;
  }

  return (
    <Stack
      height="100%"
      bgcolor="grey.100"
      p={`${resourceDetailGridPadding}px`}
    >
      {!data?.resources?.length && (
        <Stack alignItems="center" justifyContent="center" height="100%">
          <Typography variant="body2">
            No resources found based on filters.
          </Typography>
        </Stack>
      )}

      {resourceType && (
        <Fragment>
          <Stack direction="row" justifyContent="space-between" px={1}>
            <LiveResourcesGridFilterContent
              masterGridId={masterGridId}
              masterResourceType={masterResourceType}
              resourceType={resourceType}
              totalCount={data?.total_resources_count ?? 0}
              filteredCount={data?.filtered_resources_count ?? 0}
            />

            <Stack direction="row" spacing={1}>
              <LiveUsageResourceGridCsvExport
                gridApi={detailGridApi}
                resourceType={resourceType}
              />

              <AGGridColumnsToolPanel gridApi={detailGridApi} />
            </Stack>
          </Stack>

          <ResourcesAgGrid
            api={api}
            parentRowId={parentId}
            rowData={data?.resources}
            masterGridId={masterGridId}
            resourceType={resourceType}
            wrapperStyles={wrapperStyles}
            gridApiSetter={setDetailGridApi}
            masterResourceType={masterResourceType}
            onRowGroupOpened={onChildRowGroupOpened}
          />
        </Fragment>
      )}
    </Stack>
  );
};

const wrapperStyles = {
  borderRadius: 2,
  overflow: "hidden",
  border: `1px solid ${grey[300]}`,
};
